// @ts-strict-ignore
import { ArticleVO } from '@article/model/vo/article.vo';
import { Dictionary } from 'typescript-collections';
import { ArticleAccessoryInfoVO } from '../model/vo/article-accessory-info-vo';
import { Observable, of, Subject } from 'rxjs';
import { Log } from '@shared/debug/log';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUtils } from '../util/article-utils';
import { ArticleDataService } from '../service/article-data.service';

@Injectable({ providedIn: 'root' })
export class ArticleAccessoryManager {
  private _articleIdToAccessoriesMap: Dictionary<number, ArticleAccessoryInfoVO> = new Dictionary<number, ArticleAccessoryInfoVO>();
  private _loadingBaseArticleIds: number[] = [];
  private _mountingRailArticleVO: ArticleVO;

  constructor(
    private articleDataService: ArticleDataService,
  ) { }

  public get mountingRailArticleVO(): ArticleVO {
    return this._mountingRailArticleVO;
  }

  public set mountingRailArticleVO(value: ArticleVO) {
    this._mountingRailArticleVO = value
  }

  public getRotationDelta(baseArticleId: number, accessoryArticleId: number): number {
    if (!this._articleIdToAccessoriesMap.containsKey(baseArticleId)) {
      return NaN;
    }
    const articleAccessory: ArticleAccessoryInfoVO = this._articleIdToAccessoriesMap.getValue(baseArticleId);
    if (!articleAccessory || !articleAccessory.accessoryArticleIds || !articleAccessory.accessoryRotationDeltas) {
      return NaN;
    }
    const accessoryIndex: number = articleAccessory.accessoryArticleIds.indexOf(accessoryArticleId);
    if (accessoryIndex < 0 || accessoryIndex >= articleAccessory.accessoryRotationDeltas.length) {
      return NaN;
    }
    return articleAccessory.accessoryRotationDeltas[accessoryIndex];
  }

  public isAccessoryArticleExists(baseArticleId: number, accessoryArticleId: number): boolean {
    const accessoryIds: number[] = this.getAccessoryArticleIds(baseArticleId);
    return !accessoryIds ? false : accessoryIds.indexOf(accessoryArticleId) >= 0;
  }

  public getAccessoryArticleIds(baseArticleId: number): number[] {
    if (!this._articleIdToAccessoriesMap.containsKey(baseArticleId)) {
      return [];
    }
    const articleAccessory: ArticleAccessoryInfoVO = this._articleIdToAccessoriesMap.getValue(baseArticleId);
    return !articleAccessory ? [] : articleAccessory.accessoryArticleIds;
  }

  public getAccessoryArticleIdsByBaseArticleIds(baseArticleIds: number[]): number[] {
    if (!baseArticleIds || baseArticleIds.length === 0) {
      return [];
    }
    const result: number[] = [];
    for (const baseArticleId of baseArticleIds) {
      const accessoryArticleIds: number[] = this.getAccessoryArticleIds(baseArticleId);
      for (const accessoryArticleId of accessoryArticleIds) {
        if (result.indexOf(accessoryArticleId) === -1) {
          result.push(accessoryArticleId);
        }
      }
    }
    return result;
  }

  public checkAccessoryInfoByArticle(article: ArticleVO): void {
    if (!article) {
      return;
    }
    this.loadAccessoryInfoIfNotExists(article.identity);
  }

  public checkAccessoryInfoByArticles(articles: ArticleVO[]): Observable<ArticleAccessoryInfoVO[]> {
    if (!articles || articles.length === 0) {
      return of([]);
    }
    const articleIds: number[] = [];
    for (const article of articles) {
      if (!this._articleIdToAccessoriesMap.containsKey(article.identity)) {
        articleIds.push(article.identity);
      }
    }
    return this.articleDataService.loadArticleAccessoryInfos(articleIds).pipe(
     tap((data: ArticleAccessoryInfoVO[]) => {
       data.forEach((a) => {
         this._articleIdToAccessoriesMap.setValue(a.articleId, a)
       });
      })
    );
  }

  public getArticlesByHeads(articleHeads: ArticleVO[]): void {
    this.checkAccessoryInfoByArticles(articleHeads).subscribe();
  }

  public getArticleByNumbers_successHandler(articles: ArticleVO[]): void {
    this.checkAccessoryInfoByArticles(articles).subscribe();
  }

  public getLatestArticles_successHandler(articles: ArticleVO[]): void {
    this.checkAccessoryInfoByArticles(articles).subscribe();
  }

  public loadAccessoryInfoIfNotExists(articleId: number): Observable<any> {
    if (!this._articleIdToAccessoriesMap.containsKey(articleId) && this._loadingBaseArticleIds.indexOf(articleId) === -1) {
      this._loadingBaseArticleIds.push(articleId);
      return this.loadArticleAccessoryInfo(articleId);
    }
    return of(null);
  }

  private removeLoadingBaseArticleId(baseArticleId: number): void {
    const baseIdIndex: number = this._loadingBaseArticleIds.indexOf(baseArticleId);
    if (baseIdIndex === -1) {
      return;
    }
    this._loadingBaseArticleIds.splice(baseIdIndex, 1);
  }

  public articleAccessoryInfoLoaded(accessoryInfo: ArticleAccessoryInfoVO): void {
    if (!accessoryInfo) {
      return;
    }
    this.removeLoadingBaseArticleId(accessoryInfo.articleId);
    this._articleIdToAccessoriesMap.setValue(accessoryInfo.articleId, accessoryInfo);
  }

  public loadAccessoryInfo_failure(data?: any): void {
    if (data.triggerMessage && data.triggerMessage === 'LoadArticleAccessoryInfoMessage') {
      this.removeLoadingBaseArticleId(data.articleId);
    }
    Log.error('Error while loading accessory info. ', data);
  }

  private loadArticleAccessoryInfo(articleId: number): Observable<any> {
    if (NumberUtils.isNotNumber(articleId)) {
      this.loadAccessoryInfo_failure();
      return of(null);
    }
    const result: Subject<any> = new Subject();
    this.articleDataService.loadArticleAccessoryInfo(articleId).subscribe(
        (articleAccessoryInfo: ArticleAccessoryInfoVO) => {
          this.articleAccessoryInfoLoaded(articleAccessoryInfo);
          result.next(undefined);
        },
        (error: any) => {
          this.loadAccessoryInfo_failure(error);
          result.next(undefined);
        }
      );
    return result.asObservable();
  }

  public loadArticle_success(article: ArticleVO): void {
    this.storeArticle(article);
  }

  private storeArticle(article: ArticleVO): void {
    if (!article) {
      return;
    }
    if (ArticleUtils.isMountingRail(article)) {
      this._mountingRailArticleVO = article;
    }
    this.checkAccessoryInfoByArticle(article);
  }
}
